import { DetectionZone, Facility, ScreenLine, Vector2 } from "@app/shared";
import { ReactEventHandler, ReactNode, useEffect, useMemo, useState } from "react";
import { DrawingContainer } from "./DrawingContainer";
import { Shape, SvgShape } from "./LayoutDesigner/SvgShape";

export type HomographyImageEditorProps = {
    editMode: "aoi" | "homo" | "none";
    facility: Facility | undefined;
    srcImageUrl: string | undefined;
    editablePolygon?: DetectionZone | ScreenLine;
    editableAoi?: DetectionZone;
    onClientSizeChange?: (newDimensions: { width: number; height: number }) => void;
    onImgSizeChange?: (newDimensions: { width: number; height: number }) => void;
    onMouseMove?: (point: [number, number] | undefined) => any;
    onShapeCompleted?: (shape: Shape) => void;
    /** Fires for every click on the image (natural-image coordinates), regardless of editMode. */
    onImageClick?: (point: [number, number]) => void;
    /** Extra SVG overlay rendered on top of the image, in natural-image coordinates. */
    children?: ReactNode;
};

export const HomographyImageEditor: React.FC<HomographyImageEditorProps> = (props) => {
    const [srcImg, setSrcImg] = useState(null as HTMLImageElement | null);
    const [imgLoadError, setImgLoadError] = useState(null as string | null);
    const [currentPosition, setCurrentPosition] = useState<Vector2>();
    const [polyWip, setPolyWip] = useState(props.editablePolygon);
    const [aoiPoly, setAoiPoly] = useState<DetectionZone | undefined>(props.editableAoi);

    const onSourceLoad: ReactEventHandler<HTMLImageElement> = (e) => {
        props.onImgSizeChange?.({ width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight });
        setSrcImg(e.currentTarget);
    };
    const onSourceError: ReactEventHandler<HTMLImageElement> = (e) => {
        const errorEvent = e.nativeEvent as ErrorEvent;
        setImgLoadError(errorEvent?.message ?? "Failed to load img");
    };

    useEffect(() => {
        if ((srcImg?.naturalHeight ?? 0) > 0 && props.onClientSizeChange) {
            const img = srcImg!;
            props.onClientSizeChange({ width: img?.clientWidth ?? 0, height: img?.clientHeight ?? 0 });
            const resizeObserver = new ResizeObserver((entries) => props.onClientSizeChange?.({ width: img?.clientWidth ?? 0, height: img?.clientHeight ?? 0 }));
            resizeObserver.observe(img);
            return () => {
                resizeObserver.disconnect();
            };
        }
    }, [srcImg]); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setPolyWip(props.editablePolygon);
    }, [props.editablePolygon]);
    useEffect(() => {
        setAoiPoly(props.editableAoi);
    }, [props.editableAoi]);

    const handleCurrentSourcePointChange = (point: [number, number] | undefined) => {
        setCurrentPosition(point ? new Vector2({ x: point[0], y: point[1] }) : undefined);
        props.onMouseMove?.(point);
    };

    const clampPointForShape = (shape: Shape, point: Vector2) => {
        if (!shape) {
            return point;
        }
        return shape.clampPointIfClosing(point, (props.facility?.backgroundImageWidth ?? 1000) * 0.007);
    };

    const handleOnClick = (point: [number, number] | undefined) => {
        if (point) props.onImageClick?.(point);
        if (!point || props.editMode === "none") return;

        const existingShape = props.editMode === "aoi" ? aoiPoly : polyWip;
        var position = new Vector2({ x: point[0], y: point[1] });
        const clampedPosition = clampPointForShape(existingShape ?? new DetectionZone(), position);

        const newPoints = [...(existingShape?.points ?? []), clampedPosition];
        const newShape = Object.assign(new DetectionZone(), {
            ...existingShape,
            points: newPoints,
        });

        if (props.editMode === "aoi") {
            setAoiPoly(newShape);
        } else {
            setPolyWip(newShape);
        }

        if (newShape.isComplete() && props.onShapeCompleted) {
            props.onShapeCompleted(newShape);
        }
    };
    const srcImageUrl = useMemo(() => {
        if (!props.srcImageUrl || imgLoadError) {
            return undefined;
        }
        return props.srcImageUrl;
    }, [props.srcImageUrl, imgLoadError]);

    return (
        <div className="py-5">
            {!srcImageUrl && <h2 className="p-5">No image was found. Please ensure the device is taking target pictures.</h2>}
            {srcImageUrl?.length && (
                <DrawingContainer imageSrc={srcImageUrl} onImageLoad={onSourceLoad} onImageError={onSourceError} imageHeight={srcImg?.naturalHeight ?? 480} imageWidth={srcImg?.naturalWidth ?? 640} onCurrentPointChange={handleCurrentSourcePointChange} onClick={handleOnClick}>
                    {/* Dim everything outside the AOI — but only when there IS an AOI (or one is being drawn), otherwise the whole image gets veiled gray. */}
                    {(!!aoiPoly?.points?.length || props.editMode === "aoi") && <rect width="100%" height="100%" stroke="gray" fill="gray" fillOpacity=".5" strokeWidth="0" mask="url(#clip)" />}
                    {currentPosition && props.editMode === "homo" && <circle style={{ fillOpacity: 0.7, strokeWidth: 2, fill: "blue" }} r={6} cx={currentPosition.x} cy={currentPosition.y} />}
                    {polyWip && <SvgShape shape={polyWip} scaleRatio={1} currentPosition={props.editMode === "homo" && !polyWip.isComplete() ? currentPosition : undefined} />}
                    <mask id="clip">
                        <rect x="0" y="0" width="100%" height="100%" fill="white" />
                        {aoiPoly && <SvgShape opacity={1} color="black" shape={aoiPoly} scaleRatio={1} currentPosition={props.editMode === "aoi" && !aoiPoly?.isComplete() ? currentPosition : undefined} />}
                    </mask>
                    {props.children}
                </DrawingContainer>
            )}
        </div>
    );
};
